import { LOGOUT } from '@auth/graphql';
import { useAuthState } from '@auth/hooks/useAuthState';
import { clearAuth } from '@auth/state/actions';
import { logger, track } from '@core/logging';
import { routes } from '@core/routes';
import { apolloClient } from '@core/services/ApolloClient';
import { generateCorrelationId } from '@core/utils/correlationUtils';
import { LogCategory } from '@soteria/logger/browser';
import { useAppDispatch } from '@store';
import { useCallback, useState } from 'react';

/**
 * Hook to handle user logout with loading and error states
 */
export function useLogout() {
  const dispatch = useAppDispatch();
  const { user } = useAuthState();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const logout = useCallback(
    // eslint-disable-next-line complexity, max-statements
    async (redirectTo: string = routes.login): Promise<void> => {
      const correlationId = generateCorrelationId();
      const startTime = performance.now();
      const userId = user?.id;

      try {
        track('LogoutInitiated');

        setIsLoggingOut(true);
        setError(null);

        logger.info('Starting logout process', {
          category: LogCategory.AUTH,
          action: 'logout_initiate',
          userId,
          redirectTo,
          correlationId,
          timestamp: new Date().toISOString(),
        });

        // Execute logout GraphQL mutation
        const result = await apolloClient.mutate({
          mutation: LOGOUT,
        });

        if (!result.data?.logout?.success) {
          const errorMessage = result.data?.logout?.message || 'Unknown error';

          throw new Error(`Logout failed: ${errorMessage}`);
        }

        const duration = performance.now() - startTime;

        track('LogoutSucceeded');
        logger.info('Successfully completed logout process', {
          category: LogCategory.AUTH,
          action: 'logout_success',
          userId,
          redirectTo,
          correlationId,
          duration,
          logoutType: 'manual',
          timestamp: new Date().toISOString(),
        });

        // Clear auth state
        dispatch(clearAuth());

        // Redirect to specified page
        window.location.href = redirectTo;
      } catch (error) {
        const duration = performance.now() - startTime;

        track('LogoutFailed', {
          errorType: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message.substring(0, 100) : 'unknown',
        });

        logger.error('Failed to logout', {
          category: LogCategory.AUTH,
          action: 'logout_failure',
          userId,
          redirectTo,
          error:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  stack: error.stack,
                }
              : String(error),
          correlationId,
          duration,
          timestamp: new Date().toISOString(),
        });

        setError(error instanceof Error ? error.message : String(error));
        setIsLoggingOut(false);
      }
      // Note: setIsLoggingOut(false) is not called on success because the page will redirect
    },
    [dispatch, user?.id]
  );

  return {
    logout,
    isLoggingOut,
    error,
  };
}
